import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { Search } from '@lifeomic/chromicons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SwitchToTemplatingItemProvider } from '../contexts/templatingItemSwitch';
import { useAllTemplatingItemSummaries } from '../hooks/useAllTemplatingItemSummaries';
import { useFavoriteTemplatingFolders } from '../hooks/useFavoriteTemplatingFolders';
import { useFavoriteTemplatingTemplates } from '../hooks/useFavoriteTemplatingTemplates';
import { useMoveTemplatingFolder } from '../hooks/useMoveTemplatingFolder';
import { useMoveTemplatingItem } from '../hooks/useMoveTemplatingItem';
import { useTemplatingItemSummaries } from '../hooks/useTemplatingItemSummaries';
import {
  CompositeReportDefinitionSummary,
  Folder,
  ModelUtils,
  TemplateSummary,
  TemplatingItemSummary,
} from '../models';
import { FolderButton } from './Buttons/FolderButton';
import { TemplatingItemNode } from './FileSystemNode';
import { CompositeReportDefinitionNode } from './FileSystemNode/TemplatingItemNode/CompositeReportDefinitionNode';
import { TemplateNode } from './FileSystemNode/TemplatingItemNode/TemplateNode';
import { RootFileSystemTree } from './FileSystemTree';

const messages = defineMessages({
  searchAriaLabel: {
    id: 'FileSystemComponent.searchAriaLabel',
    defaultMessage: 'Search templates',
  },
  searchPlaceholder: {
    id: 'FileSystemComponent.searchPlaceholder',
    defaultMessage: 'Search templates by path...',
  },
  noSearchResults: {
    id: 'FileSystemComponent.noSearchResults',
    defaultMessage: 'No matching templates',
  },
  favoritesTitle: {
    id: 'FileSystemComponent.favoritesTitle',
    defaultMessage: 'Favorites',
  },
  everythingTitle: {
    id: 'FileSystemComponent.everythingTitle',
    defaultMessage: 'Everything',
  },
});

type TemplateNodeActions = (data: TemplateSummary) => React.ReactNode;
type CompositeReportDefinitionNodeActions = (
  data: CompositeReportDefinitionSummary,
) => React.ReactNode;

type FavoriteFolderTreeProps = {
  favoriteRootFolder: Folder;
  parentFolder: Folder;
  depth: number;
  allFolderPaths: Set<string>;
  allTemplatingItems: TemplatingItemSummary[];
  favoriteFolderExpandedState: Record<string, boolean>;
  onToggleFavoriteFolderExpanded: (folderPath: string) => void;
  templateNodeActions?: TemplateNodeActions;
  compositeReportDefinitionNodeActions?: CompositeReportDefinitionNodeActions;
};

const FavoriteFolderTree: React.FC<FavoriteFolderTreeProps> = ({
  favoriteRootFolder,
  parentFolder,
  depth,
  allFolderPaths,
  allTemplatingItems,
  favoriteFolderExpandedState,
  onToggleFavoriteFolderExpanded,
  templateNodeActions,
  compositeReportDefinitionNodeActions,
}) => {
  const directChildFolders = Array.from(allFolderPaths)
    .filter(
      folderPath =>
        folderPath.startsWith(favoriteRootFolder.path) &&
        ModelUtils.getParentFolder({ path: folderPath }).path ===
          parentFolder.path,
    )
    .map(path => ModelUtils.createFolder(path));

  const directChildItems = allTemplatingItems.filter(
    item =>
      ModelUtils.isChildOf(favoriteRootFolder, item) &&
      ModelUtils.getParentFolder(item).path === parentFolder.path,
  );

  const entries = [...directChildFolders, ...directChildItems].sort((a, b) =>
    a.path.localeCompare(b.path),
  );

  return (
    <>
      {entries.map(entry => {
        const indent = `${depth * 24}px`;
        if (entry.type === 'folder') {
          const isExpanded = favoriteFolderExpandedState[entry.path] ?? true;
          return (
            <React.Fragment key={`favorite-tree-folder-${entry.path}`}>
              <Box style={{ marginLeft: indent }}>
                <FolderButton
                  folder={entry}
                  isExpanded={isExpanded}
                  onClick={() => onToggleFavoriteFolderExpanded(entry.path)}
                  folderNodeActions={() => <></>}
                  withRowDivider={false}
                />
              </Box>
              {isExpanded && (
                <FavoriteFolderTree
                  favoriteRootFolder={favoriteRootFolder}
                  parentFolder={entry}
                  depth={depth + 1}
                  allFolderPaths={allFolderPaths}
                  allTemplatingItems={allTemplatingItems}
                  favoriteFolderExpandedState={favoriteFolderExpandedState}
                  onToggleFavoriteFolderExpanded={
                    onToggleFavoriteFolderExpanded
                  }
                  templateNodeActions={templateNodeActions}
                  compositeReportDefinitionNodeActions={
                    compositeReportDefinitionNodeActions
                  }
                />
              )}
            </React.Fragment>
          );
        }
        return (
          <Box
            key={`favorite-tree-item-${entry.id}`}
            style={{ marginLeft: indent }}
          >
            {entry.type === 'template' ? (
              <TemplateNode
                template={entry}
                templateNodeActions={templateNodeActions}
                levelOverride={depth}
                includeBaseIndent={false}
              />
            ) : (
              <CompositeReportDefinitionNode
                compositeReportDefinition={entry}
                compositeReportDefinitionNodeActions={
                  compositeReportDefinitionNodeActions
                }
                levelOverride={depth}
                includeBaseIndent={false}
              />
            )}
          </Box>
        );
      })}
    </>
  );
};

const FileSystemComponent: React.FC<{
  folderNodeActions?: (data: Folder) => React.ReactNode;
  templateNodeActions?: TemplateNodeActions;
  compositeReportDefinitionNodeActions?: CompositeReportDefinitionNodeActions;
  searchEnabled?: boolean;
}> = ({
  folderNodeActions,
  templateNodeActions,
  compositeReportDefinitionNodeActions,
  searchEnabled = false,
}) => {
  const { formatMessage } = useIntl();
  const { data: templatingItemSummaryPages } = useTemplatingItemSummaries();
  const [draggingItem, setDraggingItem] =
    useState<TemplatingItemSummary | null>(null);
  const [draggingFolder, setDraggingFolder] = useState<Folder | null>(null);
  const [favoriteFolderExpandedState, setFavoriteFolderExpandedState] =
    useState<Record<string, boolean>>({});
  const [searchValue, setSearchValue] = useState('');
  const { moveTemplatingItem } = useMoveTemplatingItem();
  const { moveTemplatingFolder } = useMoveTemplatingFolder();
  const { favoriteFolderPaths } = useFavoriteTemplatingFolders();
  const { favoriteTemplateIds, migrateFavoriteTemplateIds } =
    useFavoriteTemplatingTemplates();
  const hasSearchValue = searchValue.trim().length > 0;
  const { items: allTemplatingItems, isLoading: isLoadingSearch } =
    useAllTemplatingItemSummaries({
      enabled:
        searchEnabled ||
        favoriteTemplateIds.length > 0 ||
        favoriteFolderPaths.length > 0,
      refetchTrigger: `${favoriteFolderPaths.join(
        ',',
      )}|${favoriteTemplateIds.join(',')}`,
    });

  const templatingItemSummaries = useMemo(() => {
    return templatingItemSummaryPages?.pages.flatMap(_ => _);
  }, [templatingItemSummaryPages]);
  const filteredTemplatingItems = useMemo(() => {
    const searchTerm = searchValue.trim().toLowerCase();
    if (!searchTerm) {
      return [];
    }
    const allFolderPaths = new Set<string>();
    for (const item of allTemplatingItems) {
      const segments = item.path.split('/').filter(Boolean);
      let currentPath = '';
      for (const segment of segments.slice(0, -1)) {
        currentPath += `${segment}/`;
        allFolderPaths.add(currentPath);
      }
    }

    const matchingFolderPaths = Array.from(allFolderPaths).filter(folderPath =>
      folderPath.toLowerCase().includes(searchTerm),
    );

    const matchedItems = [...allTemplatingItems]
      .filter(item => {
        const itemPath = item.path.toLowerCase();
        if (itemPath.includes(searchTerm)) {
          return true;
        }
        return matchingFolderPaths.some(folderPath =>
          item.path.startsWith(folderPath),
        );
      })
      .sort((a, b) => a.path.localeCompare(b.path));

    const folderMap = new Map<string, Folder>();
    for (const item of matchedItems) {
      const segments = item.path.split('/').filter(Boolean);
      let currentPath = '';
      for (const segment of segments.slice(0, -1)) {
        currentPath += `${segment}/`;
        folderMap.set(currentPath, ModelUtils.createFolder(currentPath));
      }
    }

    return [...Array.from(folderMap.values()), ...matchedItems].sort((a, b) =>
      a.path.localeCompare(b.path),
    );
  }, [allTemplatingItems, searchValue]);
  useEffect(() => {
    const templates = allTemplatingItems.filter(
      (item): item is TemplateSummary => item.type === 'template',
    );
    migrateFavoriteTemplateIds(templates);
  }, [allTemplatingItems, migrateFavoriteTemplateIds]);
  const favoriteTemplates = useMemo(() => {
    if (favoriteTemplateIds.length === 0) {
      return [];
    }
    const templatesByDurableId = new Map(
      allTemplatingItems
        .filter(
          (item): item is TemplateSummary =>
            item.type === 'template' &&
            favoriteTemplateIds.includes(item.durableId),
        )
        .map(template => [template.durableId, template] as const),
    );
    return favoriteTemplateIds
      .map(id => templatesByDurableId.get(id))
      .filter((template): template is TemplateSummary => !!template);
  }, [allTemplatingItems, favoriteTemplateIds]);
  const favoriteFolders = useMemo(() => {
    return favoriteFolderPaths.map(path => ModelUtils.createFolder(path));
  }, [favoriteFolderPaths]);
  const hasFavorites =
    favoriteFolders.length > 0 || favoriteTemplates.length > 0;
  const allFolderPaths = useMemo(() => {
    const folderPaths = new Set<string>();
    for (const item of allTemplatingItems) {
      const segments = item.path.split('/').filter(Boolean);
      let currentPath = '';
      for (const segment of segments.slice(0, -1)) {
        currentPath += `${segment}/`;
        folderPaths.add(currentPath);
      }
    }
    return folderPaths;
  }, [allTemplatingItems]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingItem(event.active.data.current?.templatingItem ?? null);
    setDraggingFolder(event.active.data.current?.folder ?? null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingItem(null);
      setDraggingFolder(null);
      if (!event.over) {
        return;
      }
      const templatingItem = event.active.data.current
        ?.templatingItem as TemplatingItemSummary;
      const folder = event.over.data.current?.folder as Folder | undefined;
      if (templatingItem) {
        moveTemplatingItem(templatingItem, folder);
        return;
      }
      const sourceFolder = event.active.data.current?.folder as
        | Folder
        | undefined;
      if (sourceFolder) {
        void moveTemplatingFolder(sourceFolder, folder);
      }
    },
    [moveTemplatingFolder, moveTemplatingItem],
  );

  const toggleFavoriteFolderExpanded = useCallback((folderPath: string) => {
    setFavoriteFolderExpandedState(state => ({
      ...state,
      [folderPath]: !(state[folderPath] ?? true),
    }));
  }, []);

  return (
    <SwitchToTemplatingItemProvider>
      <Box direction="column" gap={1}>
        {searchEnabled && (
          <Box paddingX={1} paddingTop={1} flexChildren fullWidth>
            <TextField
              aria-label={formatMessage(messages.searchAriaLabel)}
              placeholder={formatMessage(messages.searchPlaceholder)}
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
              fullWidth
              startAdornment={<Search />}
            />
          </Box>
        )}
        {searchEnabled && hasSearchValue ? (
          isLoadingSearch ? (
            <Box paddingX={1} paddingY={1}>
              <Text size="caption">Loading...</Text>
            </Box>
          ) : filteredTemplatingItems.length === 0 ? (
            <Text size="caption">
              {formatMessage(messages.noSearchResults)}
            </Text>
          ) : (
            <Box direction="column">
              {filteredTemplatingItems.map(item =>
                item.type === 'folder' ? (
                  <Box
                    key={item.path}
                    style={{
                      marginLeft: `${
                        Math.max(ModelUtils.getLevel(item), 0) * 24
                      }px`,
                    }}
                  >
                    <FolderButton
                      folder={item}
                      folderNodeActions={() => <></>}
                    />
                  </Box>
                ) : (
                  <TemplatingItemNode
                    key={item.id}
                    templatingItem={item}
                    templateNodeActions={templateNodeActions}
                    compositeReportDefinitionNodeActions={
                      compositeReportDefinitionNodeActions
                    }
                  />
                ),
              )}
            </Box>
          )
        ) : (
          <>
            {hasFavorites && (
              <Box direction="column" paddingY={1}>
                <Box paddingX={1} paddingBottom={0.5}>
                  <Text size="caption">
                    {formatMessage(messages.favoritesTitle)}
                  </Text>
                </Box>
                <Box direction="column">
                  {isLoadingSearch ? (
                    <Box paddingX={1} paddingY={0.5}>
                      <DotLoader size={0} />
                    </Box>
                  ) : (
                    <>
                      {favoriteFolders.map(folder => {
                        const isExpanded =
                          favoriteFolderExpandedState[folder.path] ?? true;
                        return (
                          <React.Fragment
                            key={`favorite-root-folder-${folder.path}`}
                          >
                            <FolderButton
                              folder={folder}
                              isExpanded={isExpanded}
                              onClick={() =>
                                toggleFavoriteFolderExpanded(folder.path)
                              }
                              withRowDivider={false}
                            />
                            {isExpanded && (
                              <FavoriteFolderTree
                                favoriteRootFolder={folder}
                                parentFolder={folder}
                                depth={1}
                                allFolderPaths={allFolderPaths}
                                allTemplatingItems={allTemplatingItems}
                                favoriteFolderExpandedState={
                                  favoriteFolderExpandedState
                                }
                                onToggleFavoriteFolderExpanded={
                                  toggleFavoriteFolderExpanded
                                }
                                templateNodeActions={templateNodeActions}
                                compositeReportDefinitionNodeActions={
                                  compositeReportDefinitionNodeActions
                                }
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                      {favoriteTemplates.map(template => (
                        <TemplateNode
                          key={`favorite-${template.id}`}
                          template={template}
                          templateNodeActions={templateNodeActions}
                          levelOverride={0}
                          includeBaseIndent={false}
                        />
                      ))}
                    </>
                  )}
                </Box>
              </Box>
            )}
            {hasFavorites && (
              <Box paddingX={1} paddingBottom={0.5}>
                <Text size="caption">
                  {formatMessage(messages.everythingTitle)}
                </Text>
              </Box>
            )}
            <DndContext
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              {templatingItemSummaries && (
                <RootFileSystemTree
                  templatingItemSummaries={templatingItemSummaries}
                  folderNodeActions={folderNodeActions}
                  templateNodeActions={templateNodeActions}
                  compositeReportDefinitionNodeActions={
                    compositeReportDefinitionNodeActions
                  }
                />
              )}
              <DragOverlay>
                {draggingItem ? (
                  <TemplatingItemNode templatingItem={draggingItem} />
                ) : draggingFolder ? (
                  <FolderButton folder={draggingFolder} />
                ) : null}
              </DragOverlay>
            </DndContext>
          </>
        )}
      </Box>
    </SwitchToTemplatingItemProvider>
  );
};

export { FileSystemComponent };
